import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Shield, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const nav = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/calculators", label: "Calculators" },
  { to: "/knowledge", label: "Knowledge" },
  { to: "/contact", label: "Contact" },
];

const products = [
  { to: "/services/health", label: "Health Insurance" },
  { to: "/services/life", label: "Life Insurance" },
  { to: "/services/term", label: "Term Insurance" },
  { to: "/services/motor", label: "Motor Insurance" },
  { to: "/services/mutual-funds", label: "Mutual Funds" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all ${
        scrolled ? "glass" : "bg-white/95 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 sm:h-24 items-center justify-between">
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-2.5 group"
          >
            <img
              src="/samota-logo.png"
              alt="Samota Insurance n Investments"
              className="h-12 sm:h-14 w-auto"
            />
            <span className="hidden md:flex flex-col leading-tight border-l border-slate-200 pl-3 ml-1">
              <span className="text-[11px] text-emerald-700 italic tracking-wide">
                We insure your happiness
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {nav.map((n) =>
              n.label === "Services" ? (
                <DropdownMenu key={n.to}>
                  <DropdownMenuTrigger
                    data-testid={`nav-link-${n.label.toLowerCase()}`}
                    className="inline-flex items-center gap-1 px-3.5 py-2 rounded-lg text-sm font-medium text-slate-700 hover:text-blue-900 hover:bg-slate-50 transition"
                  >
                    {n.label} <ChevronDown className="h-3.5 w-3.5" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-60">
                    <DropdownMenuItem asChild>
                      <Link to="/services" data-testid="dropdown-all-services">
                        All Services
                      </Link>
                    </DropdownMenuItem>
                    {products.map((p) => (
                      <DropdownMenuItem key={p.to} asChild>
                        <Link to={p.to} data-testid={`dropdown-${p.to.split("/").pop()}`}>
                          {p.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`nav-link-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-3.5 py-2 rounded-lg text-sm font-medium transition ${
                      isActive
                        ? "text-blue-900 bg-blue-50/70"
                        : "text-slate-700 hover:text-blue-900 hover:bg-slate-50"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link to="/assessment">
              <Button
                data-testid="nav-cta-assessment"
                variant="outline"
                className="border-blue-900 text-blue-900 hover:bg-blue-50"
              >
                Free Assessment
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                data-testid="nav-cta-consultation"
                className="bg-blue-900 hover:bg-blue-950 text-white"
              >
                Book Consultation
              </Button>
            </Link>
          </div>

          <button
            type="button"
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center rounded-lg p-2 text-slate-700 hover:bg-slate-100"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-4" data-testid="navbar-mobile-panel">
            <div className="flex flex-col gap-1 pt-2 border-t border-slate-200">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-4 py-2.5 rounded-lg text-sm font-medium ${
                      isActive
                        ? "bg-blue-50 text-blue-900"
                        : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/assessment" className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="mobile-nav-assessment">
                Free Assessment
              </Link>
              <div className="grid grid-cols-2 gap-2 pt-3 px-1">
                <Link to="/contact" className="col-span-2">
                  <Button data-testid="mobile-cta-consultation" className="w-full bg-blue-900 text-white hover:bg-blue-950">
                    Book Consultation
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
